// ─────────────────────────────────────────────────────────────────────────────
// Membership application segments - Venue / Vendor / Boutique
// One consistent tier structure and vocabulary across all three:
//   Standard = Inclusion · Featured = Recognition · Showcase = Authority
//   Collection Partner = Influence (by invitation)
// Voice: luxury membership + editorial collection. No leads / placement /
// analytics / SaaS language. Benchmark: Leading Hotels, Soho House, Michelin.
// ─────────────────────────────────────────────────────────────────────────────

const SHOWCASE = 'https://qpkggfibwreznussudfh.supabase.co/storage/v1/object/public/showcase-media';

// Dual-directory founding line (shared lead value, all pages).
export const FOUNDING_LINE =
  'One application. Your standing across both leading luxury wedding directories: Luxury Wedding Directory and 5 Star Wedding Directory, est. 2006. Dual-directory presence is included as a founding benefit for members who join before January 2027.';

export const SEGMENTS = {
  venue: {
    key: 'venue',
    sourcePage: 'venue-pricing-application',
    leadSource: 'venue_application',
    category: 'venue',
    eyebrow: 'Venue Membership',
    h1: 'For the venues couples travel the world to discover.',
    sub: 'Membership is reserved for a select collection of exceptional wedding venues, recognised for service, reputation, and experience.',
    credibility: 'Since 2006, Luxury Wedding Directory and 5 Star Wedding Directory have connected exceptional venues with discerning couples planning celebrations across the world.',
    heroImage: 'https://images.unsplash.com/photo-1777573087931-5c6e8a69e6e4?auto=format&fit=crop&w=2000&q=85',
    sectionImage: 'https://images.unsplash.com/photo-1762177446992-4cac5b44a05d?auto=format&fit=crop&w=2400&q=85',
    whyJoin: {
      title: 'Why venues join',
      bullets: [
        'Reach affluent couples planning luxury weddings',
        'Be discovered through Aura, our AI concierge',
        'Earn editorial recognition and the LWD Seal of Excellence',
        'Tell your story through a signature Editorial Showcase',
        'Be featured across LWD Magazine',
        "Stand among the world's most exceptional venues",
      ],
    },
    tiers: [
      { name: 'Standard', stands: 'Inclusion', price: '£1,490', period: '/ year', tagline: 'Your place in the Collection',
        benefits: [
          'Inclusion in the Collection, listed across both directories',
          'Your editorial profile, gallery and story',
          'Discoverable through Aura, our AI concierge',
          'The LWD member mark',
        ] },
      { name: 'Featured', stands: 'Recognition', price: '£3,490', period: '/ year', tagline: 'Editorial recognition',
        benefits: [
          'Everything in Inclusion',
          'A featured partnership within the Collection',
          'Editorial recognition across LWD Magazine',
          'Brought forward by Aura to the couples who matter',
          'Considered first for seasonal and destination features',
        ] },
      { name: 'Showcase', stands: 'Authority', badge: "Editors' Choice", emphasis: true, price: '£6,990', period: '/ year', tagline: 'The Editorial Showcase',
        benefits: [
          'Everything in Recognition',
          'Signature storytelling, authored by our editors',
          'The LWD Seal of Excellence',
          'Presence in curated collections',
          'A dedicated editorial partner',
        ] },
      { name: 'Collection Partner', stands: 'Influence', badge: 'By Invitation', price: 'By invitation', period: '',
        benefits: [
          'A bespoke, full-house presence',
          'Named placement in curated collections',
          'Multi-region and international standing',
          'A concierge editorial partnership',
        ] },
    ],
  },

  vendor: {
    key: 'vendor',
    sourcePage: 'vendor-pricing-application',
    leadSource: 'vendor_application',
    category: 'vendor',
    eyebrow: 'Vendor Membership',
    h1: 'The names behind the weddings worth remembering.',
    sub: 'Planners, photographers, florists and stylists, selected into the Collection couples turn to. By application, and by selection.',
    credibility: 'Since 2006, Luxury Wedding Directory and 5 Star Wedding Directory have connected exceptional wedding professionals with discerning couples planning celebrations across the world.',
    heroImage: 'https://images.unsplash.com/photo-1693576587780-31fa6109191a?auto=format&fit=crop&w=2000&q=85',
    sectionImage: 'https://images.unsplash.com/photo-1714701008580-ddd230f2b028?auto=format&fit=crop&w=2400&q=85',
    whyJoin: {
      title: 'Why creatives join',
      bullets: [
        'Reach affluent couples planning luxury weddings',
        'Be discovered through Aura, our AI concierge',
        'Earn editorial recognition across LWD Magazine',
        'Have your work published in our Real Weddings stories',
        'Tell your story through a signature Magazine feature',
        'Stand among the most sought-after wedding creatives',
      ],
    },
    tiers: [
      { name: 'Standard', stands: 'Inclusion', price: '£899', period: '/ year', tagline: 'Your place in the Collection',
        benefits: [
          'Inclusion in the Collection, listed across both directories',
          'Your editorial profile, portfolio and story',
          'Discoverable through Aura, our AI concierge',
          'The LWD member mark',
        ] },
      { name: 'Featured', stands: 'Recognition', price: '£1,890', period: '/ year', tagline: 'Editorial recognition',
        benefits: [
          'Everything in Inclusion',
          'A featured partnership within the Collection',
          'Editorial recognition across LWD Magazine',
          'Brought forward by Aura to the couples who matter',
          'Considered first for editorial features',
        ] },
      { name: 'Showcase', stands: 'Authority', badge: "Editors' Choice", emphasis: true, price: '£3,490', period: '/ year', tagline: 'The Editorial Showcase',
        benefits: [
          'Everything in Recognition',
          'Signature storytelling, authored by our editors',
          'The LWD Seal of Excellence',
          'Presence in curated collections',
          'A dedicated editorial partner',
        ] },
      { name: 'Collection Partner', stands: 'Influence', badge: 'By Invitation', price: 'By invitation', period: '',
        benefits: [
          'A bespoke editorial presence',
          'Named placement in curated collections',
          'Multi-region and international standing',
          'A concierge editorial partnership',
        ] },
    ],
  },

  boutique: {
    key: 'boutique',
    sourcePage: 'boutique-pricing-application',
    leadSource: 'boutique_application',
    category: 'vendor', // listing_applications CHECK allows venue/planner/vendor; source_page distinguishes boutiques
    eyebrow: 'Boutique Membership',
    h1: 'Where the modern bride discovers what she will wear.',
    sub: 'A selective collection of bridal boutiques, couture houses, and designers, enhanced through LWD Signature Virtual Try-On.',
    credibility: 'Since 2006, Luxury Wedding Directory and 5 Star Wedding Directory have connected exceptional boutiques with discerning couples planning celebrations across the world.',
    heroImage: 'https://images.unsplash.com/photo-1597052825703-c2818b6677f8?auto=format&fit=crop&w=2000&q=85',
    sectionImage: null,
    tech: {
      eyebrow: 'LWD Signature Virtual Try-On',
      heading: 'Let her see the gown before she books.',
      line: 'Experience bridal fashion through LWD Signature Virtual Try-On, allowing couples to visualise gowns before they ever book an appointment. It is the difference between being browsed and being chosen.',
    },
    whyJoin: {
      title: 'Why boutiques join',
      bullets: [
        'Reach affluent couples planning luxury weddings',
        'Be discovered through Aura, our AI concierge',
        'Showcase your collections through Signature Virtual Try-On',
        'Tell your story through a signature Atelier Showcase',
        'Be featured across LWD Magazine',
        'Join alongside leading bridal houses and fashion designers',
      ],
    },
    tiers: [
      { name: 'Standard', stands: 'Inclusion', price: '£899', period: '/ year', tagline: 'Your place in the Collection',
        benefits: [
          'Inclusion in the Collection, listed across both directories',
          'Your boutique profile and presence in The Shop',
          'Discoverable through Aura, our AI concierge',
          'The LWD member mark',
        ] },
      { name: 'Featured', stands: 'Recognition', price: '£1,990', period: '/ year', tagline: 'Editorial recognition',
        benefits: [
          'Everything in Inclusion',
          'Signature virtual try-on, your collection brought to life',
          'A featured partnership within the fashion Collection',
          'Editorial recognition across LWD Magazine',
          'Brought forward by Aura to the couples who matter',
        ] },
      { name: 'Showcase', stands: 'Authority', badge: "Editors' Choice", emphasis: true, price: '£3,900', period: '/ year', tagline: 'The Atelier Showcase',
        benefits: [
          'Everything in Recognition',
          'The full atelier presence and Fashion Studio',
          'Signature storytelling, authored by our editors',
          'The LWD Seal of Excellence',
          'Presence in curated collections',
        ] },
      { name: 'Collection Partner', stands: 'Influence', badge: 'By Invitation', price: 'By invitation', period: '',
        benefits: [
          'A bespoke atelier presence',
          'Named placement in curated collections',
          'Multi-region and international standing',
          'A concierge editorial partnership',
        ] },
    ],
  },
};

export const FAQ = [
  { q: 'Do you accept every application?', a: 'No. Every application is reviewed personally by our editorial team to protect the standard of the Collection. Selection is intentional.' },
  { q: 'How quickly will I be part of the Collection?', a: 'Once selected, your presence is typically live within 48 hours. Showcase members take a little longer, as we craft your signature editorial story.' },
  { q: 'Can I move to a higher level later?', a: 'Yes. You may rise through the levels of standing at any time, and your new recognition takes effect immediately.' },
  { q: 'What does dual-directory presence mean?', a: 'Your standing appears across both Luxury Wedding Directory and 5 Star Wedding Directory, est. 2006. It is included as a founding benefit for members who join before January 2027.' },
];
